import React from 'react';
import { FormattedMessage } from '@kyruus/intl';
import { getGoogleMapLink } from './utils';
import messages from '../../lib/messages';
import { LocationInfoItem, LocationAddressRow } from './styles';
import { PMCConfig, ProviderMatchProviderLocationV9 } from '@kyruus/types';

interface LocationAddressProps {
  location: ProviderMatchProviderLocationV9;
}

interface LocationAddressWithDirectionLinkProps extends LocationAddressProps {
  config: PMCConfig;
}

export const LocationAddress = ({
  location
}: LocationAddressProps): React.ReactElement => {
  const { city, street1, street2, suite, zip, state } = location;
  const streetAddress = (
    <span itemProp="streetAddress">
      {[street1, street2, suite].filter(Boolean).join(', ')}
    </span>
  );
  const addressLocality = <span itemProp="addressLocality">{city}</span>;
  const addressRegion = <span itemProp="addressRegion">{state}</span>;
  const postalCode = <span itemProp="postalCode">{zip}</span>;

  return (
    <LocationAddressRow>
      {streetAddress}, {addressLocality}, {addressRegion} {postalCode}
      <meta itemProp="addressCountry" content="US" />
    </LocationAddressRow>
  );
};

const LocationAddressWithDirectionLink = ({
  config,
  location
}: LocationAddressWithDirectionLinkProps): React.ReactElement => {
  const locationId = location.primary_marketable_location_id || location.id;
  const linkId = `get-directions-${locationId}`;
  const locationNameId = `location-name-${locationId}`;
  return (
    <>
      <li>
        <LocationAddress location={location} />
      </li>

      {
        /**
          maps_config.google_routes defaults to true
          only if it is set to false do we want to suppress google routing
        */
        config.maps_config?.google_routes !== false ? (
          <li>
            <LocationAddressRow>
              <LocationInfoItem noPrint={true}>
                <a
                  id={linkId}
                  data-testid="GetDirectionsLink"
                  href={getGoogleMapLink(location)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-labelledby={`${linkId} ${locationNameId}`}
                >
                  <FormattedMessage {...messages.getDirections} />
                </a>
              </LocationInfoItem>
            </LocationAddressRow>
          </li>
        ) : null
      }
    </>
  );
};

export default LocationAddressWithDirectionLink;
